/* global __app_id, __firebase_config, __initial_auth_token */
// src/context/AuthContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithCustomToken, signInAnonymously } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase'; // Importa 'auth' y 'db' desde tu archivo firebase.js

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true); // Indica si la autenticación inicial está en curso

  // Variables globales proporcionadas por el entorno Canvas (ahora reconocidas por ESLint)
  const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
  const firebaseConfig = typeof __firebase_config !== 'undefined' ? JSON.parse(__firebase_config) : null;
  const initialAuthToken = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Usa el token de autenticación inicial si está disponible
        if (initialAuthToken) {
          await signInWithCustomToken(auth, initialAuthToken);
          console.log("Signed in with custom token.");
        } else {
          // Si no hay token inicial, inicia sesión anónimamente
          await signInAnonymously(auth);
          console.log("Signed in anonymously.");
        }
      } catch (error) {
        console.error("Error during initial Firebase sign-in:", error);
      }
    };

    // Llama a la inicialización de autenticación
    initializeAuth();

    // Listener para cambios en el estado de autenticación de Firebase
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Si hay un usuario logueado, intenta obtener su perfil de Firestore
        try {
          // Asume que los perfiles de usuario están en la colección 'usuarios'
          // y que el ID del documento es el UID del usuario de Firebase Auth
          const userDocRef = doc(db, 'usuarios', user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            setUserProfile(userDocSnap.data());
            console.log("Perfil de usuario cargado:", userDocSnap.data());
          } else {
            console.warn("No se encontró perfil de usuario para UID:", user.uid, ". Asegúrate de que el documento exista en la colección 'usuarios' y contenga un campo 'rol'.");
            setUserProfile(null);
          }
        } catch (error) {
          console.error("Error al obtener el perfil de usuario de Firestore:", error);
          setUserProfile(null);
        }
      } else {
        // Si no hay usuario, limpiar el perfil
        setUserProfile(null);
      }
      setLoading(false); // La carga inicial ha terminado
    });

    // Limpiar el listener al desmontar el componente
    return () => unsubscribe();
  }, [initialAuthToken]); // initialAuthToken como dependencia para que se ejecute si cambia

  const value = {
    currentUser,
    userProfile,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
